/**
 * 図書館検索 (Calil) お気に入り API サービス
 *
 * バックエンド /api/library-favorites を叩く軽量ラッパー。
 */

import { apiClient } from '@/api/client'
import type {
  LibraryFavorite,
  LibraryFavoriteInput,
  LibraryFavoriteStatus,
  PickupSummary,
  ReservedLibrary,
} from '@/api/types/library-favorite'

/** ログインユーザーのお気に入り一覧を新しい順で取得 */
export const listLibraryFavorites = async (): Promise<LibraryFavorite[]> => {
  const res = await apiClient.get<LibraryFavorite[]>('/library-favorites')
  return res.data
}

/** お気に入りに追加 (冪等: 登録済みなら既存レコードが返る) */
export const addLibraryFavorite = async (input: LibraryFavoriteInput): Promise<LibraryFavorite> => {
  const res = await apiClient.post<LibraryFavorite>('/library-favorites', input)
  return res.data
}

/** Amazon の本の URL から、お気に入りに登録する書誌を取得する (登録はしない) */
export const previewAmazonBook = async (url: string): Promise<LibraryFavoriteInput> => {
  const res = await apiClient.post<LibraryFavoriteInput>('/library-favorites/amazon-preview', { url })
  return res.data
}

/**
 * お気に入りの状態を更新する (reserved/returned 遷移時に対応するタイムスタンプが自動でセットされる)
 *
 * `reserved` への遷移では予約先の図書館を併せて送る (未指定なら館なしで保存される)。
 */
export const updateLibraryFavoriteStatus = async (
  isbn: string,
  status: LibraryFavoriteStatus,
  library?: ReservedLibrary | null,
): Promise<LibraryFavorite> => {
  const res = await apiClient.patch<LibraryFavorite>(`/library-favorites/${encodeURIComponent(isbn)}/status`, {
    status,
    library_id: library?.id ?? null,
    library_name: library?.name ?? null,
  })
  return res.data
}

/** 受取可の冊数を取得する */
export const getPickupSummary = async (): Promise<PickupSummary> => {
  const res = await apiClient.get<PickupSummary>('/library-favorites/pickups/summary')
  return res.data
}

/** その図書館の受取可の本をまとめて受け取ったことにする (受取 TODO も完了になる)。受け取った本を返す */
export const receiveLibraryPickups = async (libraryId: number): Promise<LibraryFavorite[]> => {
  const res = await apiClient.post<LibraryFavorite[]>(`/library-favorites/pickups/${libraryId}/receive`)
  return res.data
}

/** ISBN 指定でお気に入りを解除 (存在しない場合も 204) */
export const removeLibraryFavorite = async (isbn: string): Promise<void> => {
  await apiClient.delete(`/library-favorites/${encodeURIComponent(isbn)}`)
}
